import React, { useState, useEffect } from 'react';
import { Box, Button, Typography, IconButton, CircularProgress, Paper, Chip } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import CloudDoneIcon from '@mui/icons-material/CloudDone';
import imageUploadService, { ImageCategory } from '../../services/imageUpload.service';

interface ImageUploadProps {
  // New: برای آپلود مستقیم به ArvanCloud و دریافت URL
  onUpload?: (url: string | null, key?: string) => void;
  // Legacy: برای سازگاری با کدهای قدیمی که از File استفاده می‌کنند
  onChange?: (file: File | null) => void;
  currentImageUrl?: string;
  label?: string;
  height?: number | string;
  width?: number | string;
  accept?: string;
  maxSize?: number; // in MB
  category?: ImageCategory; // دسته‌بندی تصویر برای ArvanCloud
  useArvanCloud?: boolean; // آیا از ArvanCloud استفاده شود؟ (پیش‌فرض: true)
}

const ImageUpload: React.FC<ImageUploadProps> = ({
  onUpload,
  onChange,
  currentImageUrl,
  label = 'آپلود تصویر',
  height = 200,
  width = '100%',
  accept = 'image/*',
  maxSize = 5, // Default 5MB
  category = 'products',
  useArvanCloud = true
}) => {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [imageKey, setImageKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [isUploaded, setIsUploaded] = useState<boolean>(false);

  // Set initial preview if currentImageUrl is provided
  useEffect(() => {
    if (currentImageUrl) {
      setPreviewUrl(currentImageUrl);
      setIsUploaded(imageUploadService.isArvanCloudUrl(currentImageUrl));
    }
  }, [currentImageUrl]);

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    setError(null);
    
    if (!event.target.files || event.target.files.length === 0) {
      return;
    }
    
    const file = event.target.files[0];
    console.log('ImageUpload: File selected', { name: file.name, type: file.type, size: `${(file.size / 1024).toFixed(2)} KB` });
    
    // Check file size
    if (file.size > maxSize * 1024 * 1024) {
      setError(`حجم فایل بیش از حد مجاز است (حداکثر ${maxSize} مگابایت)`);
      return;
    }
    
    // Check file type
    if (!file.type.startsWith('image/')) {
      setError('فقط فایل‌های تصویری مجاز هستند');
      return;
    }
    
    setLoading(true);
    
    // اگر از ArvanCloud استفاده می‌کنیم و onUpload تعریف شده
    if (useArvanCloud && onUpload) {
      try {
        const result = await imageUploadService.uploadImage(file, category);
        
        if (result) {
          setPreviewUrl(result.url);
          setImageKey(result.key);
          setIsUploaded(true);
          onUpload(result.url, result.key);
          console.log('ImageUpload: Image uploaded to ArvanCloud', result);
        } else {
          setError('خطا در آپلود تصویر به سرور');
        }
      } catch (err: any) {
        setError(err.message || 'خطا در آپلود تصویر');
      } finally {
        setLoading(false);
      }
      return;
    }
    
    // حالت قدیمی (Legacy) - استفاده از File
    if (onChange) {
      const fileUrl = URL.createObjectURL(file);
      
      const img = new Image();
      img.onload = () => {
        setPreviewUrl(fileUrl);
        setIsUploaded(false);
        onChange(file);
        setLoading(false);
      };
      
      img.onerror = () => {
        setError('فایل انتخاب شده یک تصویر معتبر نیست');
        URL.revokeObjectURL(fileUrl);
        setLoading(false);
      };
      
      img.src = fileUrl;
    } else {
      setLoading(false);
    }
  };

  const handleRemove = async () => {
    // اگر تصویر در ArvanCloud آپلود شده، آن را حذف کن
    if (isUploaded && imageKey) {
      try {
        await imageUploadService.deleteImage(imageKey);
        console.log('ImageUpload: Image deleted from ArvanCloud', imageKey);
      } catch (err) {
        console.warn('Failed to delete image from ArvanCloud:', err);
      }
    }
    
    // پاک کردن preview محلی
    if (previewUrl && !currentImageUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    
    setPreviewUrl(null);
    setImageKey(null);
    setIsUploaded(false);
    
    if (onUpload) {
      onUpload(null);
    }
    if (onChange) {
      onChange(null);
    }
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Typography variant="subtitle1">
          {label}
        </Typography>
        {isUploaded && (
          <Chip
            icon={<CloudDoneIcon />}
            label="CDN"
            size="small"
            color="success"
            variant="outlined"
            sx={{ height: 22, fontSize: '0.7rem' }}
          />
        )}
      </Box>
      
      {!previewUrl ? (
        <Paper
          variant="outlined"
          component="label"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: loading ? 'wait' : 'pointer',
            height,
            width,
            p: 2,
            borderStyle: 'dashed',
            borderWidth: 2,
            borderColor: error ? 'error.main' : 'divider',
            bgcolor: 'background.default',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              borderColor: 'primary.main',
              bgcolor: 'action.hover'
            }
          }}
        >
          {loading ? (
            <Box sx={{ textAlign: 'center' }}>
              <CircularProgress size={40} sx={{ mb: 1 }} />
              <Typography variant="body2" color="textSecondary">
                در حال آپلود به سرور...
              </Typography>
            </Box>
          ) : (
            <>
              <CloudUploadIcon sx={{ fontSize: 48, mb: 1, color: 'primary.main' }} />
              <Typography variant="body1" align="center">
                تصویر را اینجا رها کنید یا کلیک کنید
              </Typography>
              <Typography variant="caption" color="textSecondary" align="center" sx={{ mt: 1 }}>
                (حداکثر {maxSize} مگابایت) - آپلود مستقیم به CDN
              </Typography>
              <input
                type="file"
                accept={accept}
                style={{ display: 'none' }}
                onChange={handleFileChange}
              />
            </>
          )}
        </Paper>
      ) : (
        <Box
          sx={{
            position: 'relative',
            height,
            width,
            display: 'flex',
            justifyContent: 'center',
            mb: 2
          }}
        >
          <Box
            component="img"
            src={previewUrl}
            alt="Preview"
            sx={{
              height: '100%',
              maxWidth: '100%',
              objectFit: 'contain',
              borderRadius: 1
            }}
          />
          <IconButton
            aria-label="remove"
            onClick={handleRemove}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              bgcolor: 'background.paper',
              boxShadow: 1,
              '&:hover': {
                bgcolor: 'error.light',
                color: 'white'
              }
            }}
          >
            <DeleteIcon />
          </IconButton>
          
          <Button
            variant="contained"
            component="label"
            size="small"
            disabled={loading}
            sx={{
              position: 'absolute',
              bottom: 8,
              right: 8
            }}
          >
            {loading ? <CircularProgress size={20} /> : 'تغییر تصویر'}
            <input
              type="file"
              accept={accept}
              style={{ display: 'none' }}
              onChange={handleFileChange}
            />
          </Button>
        </Box>
      )}
      
      {error && (
        <Typography color="error" variant="caption" sx={{ mt: 1, display: 'block' }}>
          {error}
        </Typography>
      )}
    </Box>
  );
};

export default ImageUpload;
